import React, { useState, useEffect, useRef } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  updatePassword
} from 'firebase/auth';
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  orderBy,
  Timestamp,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import Sidebar from '../components/Sidebar/Sidebar';
import { useAdminRole } from '../hooks/useAdminRole';
import { recordAdminLogin } from '../utils/adminLoginTracker';
import './AdminPage.css';

const isDev = typeof window !== 'undefined' && window.location.hostname === 'localhost';

const AdminPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [reservations, setReservations] = useState([]);
  const [reservationHistory, setReservationHistory] = useState([]);
  const [loadingReservations, setLoadingReservations] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active' ou 'history'
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    emailOrPhone: '',
    date: '',
    time: '',
    persons: '2',
    note: ''
  });
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const { role: userRole } = useAdminRole();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [, setUserData] = useState(null);

  // Référence pour éviter les enregistrements multiples
  const loginRecordedRef = useRef(new Set());

  // Charger les données utilisateur depuis Firestore
  const loadUserData = async (userId) => {
    try {
      const userDocRef = doc(db, 'adminUsers', userId);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        const data = userDocSnap.data();
        setUserData(data);
        return data;
      } else {
        // Si l'utilisateur n'existe pas dans Firestore, le créer avec les valeurs par défaut
        const defaultUserData = {
          email: auth.currentUser?.email || '',
          uid: userId,
          role: 1,
          firstLogin: true,
          createdAt: Timestamp.now(),
          passwordChanged: false
        };
        await setDoc(userDocRef, defaultUserData);
        setUserData(defaultUserData);
        return defaultUserData;
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données utilisateur:', error);
      return null;
    }
  };

  // Vérifier l'état d'authentification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      if (currentUser) {
        // Charger les données utilisateur
        const userData = await loadUserData(currentUser.uid);
        
        // Vérifier si c'est la première connexion et que le mot de passe n'a pas été changé
        if (userData && userData.firstLogin && !userData.passwordChanged) {
          setShowPasswordChangeModal(true);
        }
        
        // Enregistrer la connexion seulement si pas déjà fait pour cette session
        if (!loginRecordedRef.current.has(currentUser.uid)) {
          loginRecordedRef.current.add(currentUser.uid);
          try {
            await recordAdminLogin(currentUser.email, currentUser.uid);
          } catch (error) {
            console.error('Erreur lors de l\'enregistrement de la connexion:', error);
          }
        }
        
        // Charger les données
        checkAndMovePastReservations().then(() => {
          loadReservations();
          loadReservationHistory();
        });
      } else {
        setUserData(null);
        setShowPasswordChangeModal(false);
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Vérifier si une réservation est passée
  const isReservationPast = (reservation) => {
    if (!reservation.date || !reservation.time) return false;
    
    try {
      // Créer une date à partir de la date et de l'heure de la réservation
      const reservationDate = new Date(reservation.date);
      const [hours, minutes] = reservation.time.split(':').map(Number);
      reservationDate.setHours(hours, minutes, 0, 0);
      
      // Comparer avec la date/heure actuelle
      const now = new Date();
      return reservationDate < now;
    } catch (error) {
      console.error('Erreur lors de la vérification de la date:', error);
      return false;
    }
  };

  // Vérifier et déplacer les réservations passées vers l'historique
  const checkAndMovePastReservations = async () => {
    try {
      const reservationsRef = collection(db, 'reservations');
      const querySnapshot = await getDocs(reservationsRef);
      
      const pastReservations = [];
      querySnapshot.forEach((docSnapshot) => {
        const reservation = {
          id: docSnapshot.id,
          ...docSnapshot.data()
        };
        
        if (isReservationPast(reservation)) {
          pastReservations.push(reservation);
        }
      });

      // Déplacer les réservations passées vers l'historique
      if (pastReservations.length > 0) {
        const historyRef = collection(db, 'reservation_history');
        
        for (const reservation of pastReservations) {
          // Ajouter à l'historique
          const { id, ...reservationData } = reservation;
          await addDoc(historyRef, {
            ...reservationData,
            movedToHistoryAt: Timestamp.now()
          });
          
          // Supprimer de la collection active
          await deleteDoc(doc(db, 'reservations', id));
        }
        
        console.log(`✅ ${pastReservations.length} réservation(s) déplacée(s) vers l'historique`);
      }
    } catch (error) {
      console.error('Erreur lors de la vérification des réservations passées:', error);
    }
  };

  // Charger les réservations actives depuis Firestore
  const loadReservations = async () => {
    setLoadingReservations(true);
    try {
      const reservationsRef = collection(db, 'reservations');
      const q = query(reservationsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const reservationsData = [];
      querySnapshot.forEach((doc) => {
        reservationsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setReservations(reservationsData);
    } catch (error) {
      console.error('Erreur lors du chargement des réservations:', error);
      setError('Erreur lors du chargement des réservations');
    } finally {
      setLoadingReservations(false);
    }
  };

  // Charger l'historique des réservations depuis Firestore
  const loadReservationHistory = async () => {
    try {
      const historyRef = collection(db, 'reservation_history');
      const q = query(historyRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const historyData = [];
      querySnapshot.forEach((doc) => {
        historyData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setReservationHistory(historyData);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
    }
  };

  // Supprimer une réservation de l'historique
  const handleDeleteReservation = async (reservationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette réservation de l\'historique ?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'reservation_history', reservationId));
      await loadReservationHistory();
      setError('');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setError('Erreur lors de la suppression de la réservation');
    }
  };

  // Supprimer une réservation active
  const handleDeleteActiveReservation = async (reservationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette réservation ?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'reservations', reservationId));
      await loadReservations();
      setOpenMenuId(null);
      setError('');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setError('Erreur lors de la suppression de la réservation');
    }
  };

  // Fermer le menu si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && !event.target.closest('.reservation-menu-container')) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openMenuId]);

  // Ouvrir le modal de modification
  const handleEditReservation = (reservation) => {
    setEditingReservation(reservation);
    setEditFormData({
      name: reservation.name || '',
      emailOrPhone: reservation.emailOrPhone || '',
      date: reservation.date instanceof Timestamp 
        ? reservation.date.toDate().toISOString().split('T')[0]
        : reservation.date || '',
      time: reservation.time || '',
      persons: reservation.persons || '2',
      note: reservation.note || ''
    });
  };

  // Fermer le modal de modification
  const handleCloseEditModal = () => {
    setEditingReservation(null);
    setEditFormData({
      name: '',
      emailOrPhone: '',
      date: '',
      time: '',
      persons: '2',
      note: ''
    });
  };

  // Sauvegarder les modifications
  const handleSaveEdit = async () => {
    if (!editingReservation) return;

    try {
      // Obtenir l'ancienne date au format string pour comparaison
      let oldDateString = '';
      if (editingReservation.date instanceof Timestamp) {
        oldDateString = editingReservation.date.toDate().toISOString().split('T')[0];
      } else if (typeof editingReservation.date === 'string') {
        oldDateString = editingReservation.date.split('T')[0];
      } else {
        oldDateString = new Date(editingReservation.date).toISOString().split('T')[0];
      }

      const oldTime = editingReservation.time || '';
      
      // Vérifier si la date ou l'heure a été modifiée
      const dateChanged = editFormData.date !== oldDateString;
      const timeChanged = editFormData.time !== oldTime;
      const dateOrTimeChanged = dateChanged || timeChanged;

      // Créer la nouvelle date/heure complète
      const newDate = new Date(editFormData.date);
      const [hours, minutes] = editFormData.time.split(':').map(Number);
      newDate.setHours(hours, minutes, 0, 0);
      
      const now = new Date();
      const isFuture = newDate > now;

      const updatedData = {
        name: editFormData.name,
        emailOrPhone: editFormData.emailOrPhone,
        date: editFormData.date,
        time: editFormData.time,
        persons: editFormData.persons,
        note: editFormData.note
      };

      // Si la date/heure a été modifiée et est dans le futur, déplacer vers réservations actives
      if (dateOrTimeChanged && isFuture) {
        // Ajouter à la collection active
        const reservationsRef = collection(db, 'reservations');
        await addDoc(reservationsRef, {
          ...updatedData,
          createdAt: editingReservation.createdAt || Timestamp.now()
        });

        // Supprimer de l'historique
        await deleteDoc(doc(db, 'reservation_history', editingReservation.id));
        
        // Recharger les données
        await loadReservations();
        await loadReservationHistory();
        handleCloseEditModal();
        setError('');
        return;
      }

      // Sinon, mettre à jour dans l'historique
      await updateDoc(doc(db, 'reservation_history', editingReservation.id), updatedData);
      await loadReservationHistory();
      handleCloseEditModal();
      setError('');
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      setError('Erreur lors de la modification de la réservation');
    }
  };

  // Connexion
  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      // Enregistrer la connexion immédiatement après succès
      if (userCredential.user) {
        try {
          await recordAdminLogin(userCredential.user.email, userCredential.user.uid);
        } catch (loginError) {
          console.error('Erreur lors de l\'enregistrement de la connexion:', loginError);
        }
      }
      // L'état utilisateur sera mis à jour via onAuthStateChanged
    } catch (error) {
      console.error('Erreur de connexion:', error);
      if (error.code === 'auth/user-not-found') {
        setError('Aucun compte trouvé avec cet email');
      } else if (error.code === 'auth/wrong-password') {
        setError('Mot de passe incorrect');
      } else if (error.code === 'auth/invalid-email') {
        setError('Email invalide');
      } else {
        setError('Erreur de connexion: ' + error.message);
      }
    }
  };

  // Inscription
  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const newUser = userCredential.user;
      
      // Enregistrer les informations de l'utilisateur dans Firestore
      const userData = {
        email: newUser.email,
        uid: newUser.uid,
        role: 1, // Rôle par défaut
        firstLogin: true, // Première connexion
        createdAt: Timestamp.now(),
        passwordChanged: false // Le mot de passe n'a pas encore été changé
      };
      
      // Utiliser setDoc avec l'UID comme ID du document
      await setDoc(doc(db, 'adminUsers', newUser.uid), userData);
      
      // Déconnecter immédiatement : l'admin crée le compte pour un autre utilisateur
      // qui se connectera lui-même et changera son mot de passe
      await signOut(auth);
      
      console.log('Utilisateur admin créé avec succès:', userData);
      setError('');
      setEmail('');
      setPassword('');
      setIsSignUp(false);
      alert('Compte créé avec succès. Envoyez les identifiants à l\'utilisateur pour qu\'il se connecte et change son mot de passe.');
    } catch (error) {
      console.error('Erreur d\'inscription:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError('Cet email est déjà utilisé');
      } else if (error.code === 'auth/invalid-email') {
        setError('Email invalide');
      } else if (error.code === 'auth/weak-password') {
        setError('Le mot de passe est trop faible');
      } else {
        setError('Erreur d\'inscription: ' + error.message);
      }
    }
  };

  // Changer le mot de passe lors de la première connexion
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordChangeError('');
    
    if (!newPassword || !confirmPassword) {
      setPasswordChangeError('Veuillez remplir tous les champs');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordChangeError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordChangeError('Les mots de passe ne correspondent pas');
      return;
    }
    
    try {
      const user = auth.currentUser;
      if (!user) {
        setPasswordChangeError('Utilisateur non connecté');
        return;
      }
      
      // Mettre à jour le mot de passe
      await updatePassword(user, newPassword);
      
      // Mettre à jour les données utilisateur dans Firestore
      const userDocRef = doc(db, 'adminUsers', user.uid);
      await updateDoc(userDocRef, {
        passwordChanged: true,
        firstLogin: false,
        passwordChangedAt: Timestamp.now()
      });
      
      // Recharger les données utilisateur
      await loadUserData(user.uid);
      
      // Fermer le modal
      setShowPasswordChangeModal(false);
      setNewPassword('');
      setConfirmPassword('');
      
      console.log('Mot de passe changé avec succès');
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      if (error.code === 'auth/weak-password') {
        setPasswordChangeError('Le mot de passe est trop faible');
      } else if (error.code === 'auth/requires-recent-login') {
        setPasswordChangeError('Veuillez vous reconnecter pour changer votre mot de passe');
      } else {
        setPasswordChangeError('Erreur lors du changement de mot de passe: ' + error.message);
      }
    }
  };

  // Formater la date
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    if (dateValue instanceof Timestamp) {
      const date = dateValue.toDate();
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    if (typeof dateValue === 'string') {
      const date = new Date(dateValue);
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    return dateValue.toString();
  };

  // Formater l'heure de création
  const formatCreatedAt = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    if (timestamp instanceof Timestamp) {
      const date = timestamp.toDate();
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    return timestamp.toString();
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  // Si l'utilisateur n'est pas connecté, afficher le formulaire de connexion/inscription
  if (!user) {
    return (
      <div className="admin-page">
        <div className="auth-container">
          <div className="auth-card">
            <h1 className="auth-title">Administration Aqua Farina</h1>
            
            <div className="auth-tabs">
              <button
                className={`auth-tab ${!isSignUp ? 'active' : ''}`}
                onClick={() => {
                  setIsSignUp(false);
                  setError('');
                  setEmail('');
                  setPassword('');
                }}
              >
                Connexion
              </button>
              {isDev && (
                <button
                  className={`auth-tab ${isSignUp ? 'active' : ''}`}
                  onClick={() => {
                    setIsSignUp(true);
                    setError('');
                    setEmail('');
                    setPassword('');
                  }}
                >
                  Inscription
                </button>
              )}
            </div>

            <form onSubmit={(isSignUp && isDev) ? handleSignUp : handleSignIn} className="auth-form">
              {error && <div className="error-message">{error}</div>}
              
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Mot de passe</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>

              <button type="submit" className="auth-submit-btn">
                {(isSignUp && isDev) ? 'Créer un compte' : 'Se connecter'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Si l'utilisateur est connecté, afficher le dashboard
  return (
    <div className="admin-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} userRole={userRole} />
      
      <div className="admin-content-wrapper">
        <div className="admin-header">
          <button 
            className="sidebar-toggle-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle sidebar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <h1>Tableau de bord - Réservations</h1>
      </div>

      <div className="admin-content">
        {error && <div className="error-message">{error}</div>}
        
        {/* Onglets pour basculer entre actives et historique */}
        <div className="reservations-tabs">
          <button
            className={`tab-button ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            Réservations Actives ({reservations.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Historique ({reservationHistory.length})
          </button>
        </div>

        <div className="reservations-stats">
          <div className="stat-card">
            <h3>Actives</h3>
            <p className="stat-number">{reservations.length}</p>
          </div>
          <div className="stat-card">
            <h3>Historique</h3>
            <p className="stat-number">{reservationHistory.length}</p>
          </div>
          <div className="stat-card">
            <h3>Total</h3>
            <p className="stat-number">{reservations.length + reservationHistory.length}</p>
          </div>
        </div>

        {loadingReservations ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Chargement des réservations...</p>
          </div>
        ) : (
          <>
            {/* Réservations Actives */}
            {activeTab === 'active' && (
              reservations.length === 0 ? (
                <div className="empty-state">
                  <p>Aucune réservation active pour le moment</p>
                </div>
              ) : (
                <div className="reservations-list">
                  {reservations.map((reservation) => (
                    <div key={reservation.id} className="reservation-card active-reservation">
                      <div className="reservation-header">
                        <h3>{reservation.name || 'Sans nom'}</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span className="reservation-date-badge active-badge">
                            {formatDate(reservation.date)}
                          </span>
                          <div className="reservation-menu-container">
                            <button
                              className="reservation-menu-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId(openMenuId === reservation.id ? null : reservation.id);
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="1"></circle>
                              <circle cx="12" cy="5" r="1"></circle>
                              <circle cx="12" cy="19" r="1"></circle>
                            </svg>
                            </button>
                            {openMenuId === reservation.id && (
                              <div className="reservation-menu-dropdown">
                                <button
                                  className="reservation-menu-item delete-item"
                                  onClick={() => handleDeleteActiveReservation(reservation.id)}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6"></polyline>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                  </svg>
                                  Supprimer
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="reservation-details">
                        <div className="detail-row">
                          <span className="detail-label">Email/Téléphone:</span>
                          <span className="detail-value">{reservation.emailOrPhone || 'N/A'}</span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-label">Date:</span>
                          <span className="detail-value">{formatDate(reservation.date)}</span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-label">Heure:</span>
                          <span className="detail-value">{reservation.time || 'N/A'}</span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-label">Nombre de personnes:</span>
                          <span className="detail-value">{reservation.persons || '2'} personnes</span>
                        </div>
                        
                        {reservation.note && (
                          <div className="detail-row">
                            <span className="detail-label">Note:</span>
                            <span className="detail-value note-text">{reservation.note}</span>
                          </div>
                        )}
                        
                        <div className="detail-row">
                          <span className="detail-label">Créée le:</span>
                          <span className="detail-value created-at">
                            {formatCreatedAt(reservation.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            {/* Historique des Réservations */}
            {activeTab === 'history' && (
              reservationHistory.length === 0 ? (
                <div className="empty-state">
                  <p>Aucune réservation dans l'historique</p>
                </div>
              ) : (
                <div className="reservations-list">
                  {reservationHistory.map((reservation) => (
                    <div key={reservation.id} className="reservation-card history-reservation">
                      <div className="reservation-header">
                        <h3>{reservation.name || 'Sans nom'}</h3>
                        <span className="reservation-date-badge history-badge">
                          {formatDate(reservation.date)}
                        </span>
                      </div>
                      
                      <div className="reservation-details">
                        <div className="detail-row">
                          <span className="detail-label">Email/Téléphone:</span>
                          <span className="detail-value">{reservation.emailOrPhone || 'N/A'}</span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-label">Date:</span>
                          <span className="detail-value">{formatDate(reservation.date)}</span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-label">Heure:</span>
                          <span className="detail-value">{reservation.time || 'N/A'}</span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-label">Nombre de personnes:</span>
                          <span className="detail-value">{reservation.persons || '2'} personnes</span>
                        </div>
                        
                        {reservation.note && (
                          <div className="detail-row">
                            <span className="detail-label">Note:</span>
                            <span className="detail-value note-text">{reservation.note}</span>
                          </div>
                        )}
                        
                        <div className="detail-row">
                          <span className="detail-label">Créée le:</span>
                          <span className="detail-value created-at">
                            {formatCreatedAt(reservation.createdAt)}
                          </span>
                        </div>
                        
                        {reservation.movedToHistoryAt && (
                          <div className="detail-row">
                            <span className="detail-label">Déplacée vers l'historique le:</span>
                            <span className="detail-value created-at">
                              {formatCreatedAt(reservation.movedToHistoryAt)}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="reservation-actions">
                        <button
                          onClick={() => handleEditReservation(reservation)}
                          className="edit-btn"
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDeleteReservation(reservation.id)}
                          className="delete-btn"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </>
        )}
      </div>
      </div>

      {/* Modal de modification */}
      {editingReservation && (
        <div className="modal-overlay" onClick={handleCloseEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Modifier la réservation</h2>
              <button className="modal-close-btn" onClick={handleCloseEditModal}>
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="edit-name">Nom</label>
                <input
                  type="text"
                  id="edit-name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  placeholder="Nom du client"
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-emailOrPhone">Email/Téléphone</label>
                <input
                  type="text"
                  id="edit-emailOrPhone"
                  value={editFormData.emailOrPhone}
                  onChange={(e) => setEditFormData({ ...editFormData, emailOrPhone: e.target.value })}
                  placeholder="Email ou téléphone"
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-date">Date</label>
                <input
                  type="date"
                  id="edit-date"
                  value={editFormData.date}
                  onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-time">Heure</label>
                <input
                  type="time"
                  id="edit-time"
                  value={editFormData.time}
                  onChange={(e) => setEditFormData({ ...editFormData, time: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-persons">Nombre de personnes</label>
                <input
                  type="number"
                  id="edit-persons"
                  min="1"
                  max="60"
                  value={editFormData.persons}
                  onChange={(e) => setEditFormData({ ...editFormData, persons: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-note">Note</label>
                <textarea
                  id="edit-note"
                  value={editFormData.note}
                  onChange={(e) => setEditFormData({ ...editFormData, note: e.target.value })}
                  placeholder="Note optionnelle"
                  rows="3"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="modal-cancel-btn" onClick={handleCloseEditModal}>
                Annuler
              </button>
              <button className="modal-save-btn" onClick={handleSaveEdit}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de changement de mot de passe (première connexion) */}
      {showPasswordChangeModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Changer votre mot de passe</h2>
            </div>
            
            <div className="modal-body">
              <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
                C'est votre première connexion. Veuillez définir un nouveau mot de passe pour sécuriser votre compte.
              </p>
              
              {passwordChangeError && (
                <div className="error-message" style={{ 
                  background: '#fef2f2', 
                  color: '#dc2626', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  marginBottom: '20px',
                  border: '1px solid #fecaca'
                }}>
                  {passwordChangeError}
                </div>
              )}
              
              <form onSubmit={handlePasswordChange}>
                <div className="form-group">
                  <label htmlFor="new-password">Nouveau mot de passe</label>
                  <input
                    type="password"
                    id="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Entrez votre nouveau mot de passe"
                    required
                    minLength={6}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirm-password">Confirmer le mot de passe</label>
                  <input
                    type="password"
                    id="confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirmez votre nouveau mot de passe"
                    required
                    minLength={6}
                  />
                </div>

                <div className="modal-footer" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button 
                    type="submit" 
                    className="modal-save-btn"
                    style={{ width: '100%' }}
                  >
                    Enregistrer le nouveau mot de passe
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      setShowPasswordChangeModal(false);
                      await signOut(auth);
                    }}
                    className="modal-cancel-btn"
                    style={{ 
                      width: '100%', 
                      padding: '10px 16px', 
                      background: '#6b7280', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '8px', 
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Se déconnecter
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;

